import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { csAdminApi } from '../../../services/api';
import { mapCompanyFromApi } from '../../../utils/apiMappers';
const getTypeColor = (type) => {
  const normalized = String(type || '').toUpperCase();
  if (normalized.includes('HOLD') || normalized === 'PARENT') return 'primary';
  if (normalized.includes('SUB')) return 'success';
  if (normalized.includes('ASSOC')) return 'warning';
  return 'default';
};

const mapCompanyRow = (row) => {
  const mapped = mapCompanyFromApi(row);
  if (!mapped) return null;
  return {
    id: mapped.id,
    name: mapped.name,
    tradeLicenseNumber: mapped.tradeLicenseNumber || '—',
    type: mapped.type || '—',
    status: mapped.status === 'ACTIVE' ? 'Active' : 'Inactive',
    directorCount: mapped.director_count ?? 0,
  };
};

const SelectCompanyStep = ({ selectedCompanyId, onSelectCompany, errors }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCompanies(companies);
      return;
    }
    const query = searchQuery.toLowerCase();
    setFilteredCompanies(
      companies.filter((company) =>
        company.name.toLowerCase().includes(query)
        || company.tradeLicenseNumber?.toLowerCase().includes(query)
        || company.type?.toLowerCase().includes(query)
      )
    );
  }, [searchQuery, companies]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await csAdminApi.getCompanies();
      const list = (response.data?.data || [])
        .map(mapCompanyRow)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));

      setCompanies(list);
      setFilteredCompanies(list);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies');
      setCompanies([]);
      setFilteredCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = (company) => {
    onSelectCompany(company.id, company.name);
    setError('');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Company
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Choose the company to add a director to
      </Typography>

      {errors.companyId && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.companyId}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search by company name or trade license..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading companies...
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Available Companies ({filteredCompanies.length}):
          </Typography>
          <List
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 400,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {filteredCompanies.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No companies found"
                  secondary={companies.length === 0
                    ? 'No companies are available in your scope yet'
                    : 'Try adjusting your search'}
                  sx={{ textAlign: 'center', py: 2 }}
                />
              </ListItem>
            ) : (
              filteredCompanies.map((company) => (
                <ListItem
                  key={company.id}
                  button
                  selected={String(selectedCompanyId) === String(company.id)}
                  onClick={() => handleSelectCompany(company)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={String(selectedCompanyId) === String(company.id)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body1" fontWeight={500}>
                          {company.name}
                        </Typography>
                        <Chip
                          label={company.type}
                          size="small"
                          color={getTypeColor(company.type)}
                          variant="outlined"
                        />
                      </Box>
                    )}
                    secondary={(
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Trade License: {company.tradeLicenseNumber} • {company.directorCount} directors
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status: {company.status}
                        </Typography>
                      </Box>
                    )}
                  />
                </ListItem>
              ))
            )}
          </List>
        </>
      )}

      {selectedCompanyId && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
          <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ mb: 0.5 }}>
            Selected Company
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {filteredCompanies.find((c) => String(c.id) === String(selectedCompanyId))?.name
              || companies.find((c) => String(c.id) === String(selectedCompanyId))?.name}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SelectCompanyStep;
